import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Reveal } from "@/components/Reveal";

const slides = [
  { img: "/assets/gallery/combi-oven.webp", caption: "Combi Station" },
  { img: "/assets/gallery/equipment-lineup.webp", caption: "Equipment Lineup" },
  { img: "/assets/gallery/freezers.webp", caption: "Cold Storage" },
  { img: "/assets/gallery/extraction-hood.webp", caption: "Extraction & Ventilation" },
  { img: "/assets/gallery/prep-line.webp", caption: "Prep Line" },
  { img: "/assets/gallery/spray-sink.webp", caption: "Wash Stations" },
];

const bullets = [
  "Flexible rental for every stage",
  "Hygiene, safety & compliance first",
  "A community of food entrepreneurs",
  "Delivery, marketing & business support",
];

export default function Concept() {
  const [index, setIndex] = useState(0);

  useEffect(() => {
    const id = setInterval(() => setIndex((i) => (i + 1) % slides.length), 4500);
    return () => clearInterval(id);
  }, []);

  const slide = slides[index];

  return (
    <section id="concept" className="py-24 md:py-32" data-testid="concept-section">
      <div className="mx-auto grid max-w-7xl items-start gap-14 px-5 md:grid-cols-2 md:px-8 lg:gap-20">
        <Reveal className="relative">
          <div className="relative h-[480px] overflow-hidden rounded-2xl border border-gold/20 shadow-[0_40px_80px_-20px_rgba(0,0,0,0.8)] md:h-[600px]">
            <AnimatePresence mode="sync">
              <motion.img
                key={slide.img}
                src={slide.img}
                alt={`Royal Express DIP facility — ${slide.caption}`}
                initial={{ opacity: 0, scale: 1.06 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 1.4, ease: [0.22, 1, 0.36, 1] }}
                className="absolute inset-0 h-full w-full object-cover"
                data-testid="concept-carousel-image"
              />
            </AnimatePresence>
            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-black/10" aria-hidden="true" />
            <span className="absolute bottom-5 left-5 rounded-sm border border-gold/50 bg-black/70 px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.22em] text-gold backdrop-blur-md" data-testid="concept-carousel-caption">
              {String(index + 1).padStart(2, "0")} — {slide.caption}
            </span>
          </div>
          <div className="mt-5 flex gap-2" data-testid="concept-carousel-dots">
            {slides.map((s, i) => (
              <button
                key={s.img}
                onClick={() => setIndex(i)}
                aria-label={`Show ${s.caption}`}
                data-testid={`concept-dot-${i}`}
                className={`h-1 rounded-full transition-all duration-500 ${i === index ? "w-8 bg-gold" : "w-3 bg-gold/25 hover:bg-gold/50"}`}
              />
            ))}
          </div>
        </Reveal>

        <div>
          <Reveal>
            <p className="text-[11px] font-semibold uppercase tracking-[0.34em] text-gold" data-testid="concept-eyebrow">The Concept</p>
            <h2 className="mt-4 font-serif text-4xl font-medium leading-[1.05] tracking-tight text-white md:text-5xl" data-testid="concept-heading">
              Cook. <span className="italic text-gold">Grow.</span> Scale.
            </h2>
          </Reveal>
          <Reveal delay={0.12}>
            <div className="mt-8 space-y-7 border-t border-gold/10 pt-8" data-testid="concept-copy">
              <p className="text-sm font-light leading-relaxed text-white/75 md:text-base">
                Royal Express Kitchen Units Center is a modern cloud kitchen rental facility built for Dubai's
                fast-growing food delivery ecosystem. We hand you a{" "}
                <span className="font-semibold text-white">ready-to-use commercial kitchen</span> — fully fitted,
                fully compliant — so you can focus on the food, not the fit-out.
              </p>
              <p className="border-t border-gold/10 pt-7 text-sm font-light leading-relaxed text-white/75 md:text-base">
                Whether you're a <span className="font-semibold text-white">home chef taking the next step</span>,
                a start-up testing a new concept, or an established brand expanding into delivery, our flexible
                kitchen units are designed to help you grow efficiently and with confidence.
              </p>
              <p className="border-t border-gold/10 pt-7 text-sm font-light leading-relaxed text-white/75 md:text-base">
                Our mission is simple — offer flexible kitchen plans for every stage, maintain the highest standard
                of hygiene and safety, and back a genuine{" "}
                <span className="font-semibold text-white">community of food entrepreneurs</span> with the support
                to help their brands actually grow.
              </p>
            </div>
          </Reveal>
          <Reveal delay={0.2}>
            <ul className="mt-9 grid gap-x-8 gap-y-4 sm:grid-cols-2" data-testid="concept-bullets">
              {bullets.map((b) => (
                <li key={b} className="flex items-start gap-3 text-sm text-white/80">
                  <span className="mt-1.5 h-2 w-2 shrink-0 rotate-45 bg-gold" aria-hidden="true" />
                  {b}
                </li>
              ))}
            </ul>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
